import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes } from '@bloomberg/media-ui-core';
import type MediaServicesSearchClient from '@foundation/media-services-search-client/lib/index';
import FocusTrap from 'focus-trap-react';

import {
  USER_OPTIONS_MENU_ITEMS_LOGGED_IN,
  USER_OPTIONS_MENU_ITEMS_SUBSCRIBER,
  USER_OPTIONS_MENU_ITEMS_SUBSCRIBER_WITH_HUB_ACCESS,
} from '../constants/constants';
import { JAPAN_LOCALE_STRING } from '../i18n';
import { SignInButtons } from '../LogoBar';
import { LogoBarMobile } from '../LogoBarMobile';
import { MenuMobile } from '../MenuMobile';
import { DEFAULT_USER } from '../Nav';
import { useNavContext } from '../Nav/NavContext';
import { RegionPickerMobile } from '../RegionPickerMobile';
import { SearchMobile } from '../SearchMobile';
import { SearchMobileJapan } from '../SearchMobile/SearchMobileJapan';
import {
  LANGUAGES,
  MenuItem,
  REGIONS,
  SubscriptionData,
  THEMES,
  User,
} from '../types';
import { UserOptionsDropdownMobile } from '../UserOptionsDropdownMobile';
import { getLocalizedMenuSections } from '../utils/getLocalizedMenuSections';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './NavMobile.module.css';

export type NavMobileProps = GlobalProps & {
  className?: string;
  initializeSignInWithRegUI: (signInButtons: SignInButtons) => void;
  onSignOut: () => void;
  isWeekend?: boolean;
  language?: LANGUAGES;
  name?: string;
  region?: REGIONS;
  searchClient: MediaServicesSearchClient;
  theme?: THEMES;
  useSubmenu?: boolean;
  user?: User;
  menuItems: Record<string, MenuItem>;
  trackingFunction: (action: string, eventLabel: string) => void;
  countryCode?: string;
  subscriptionUrl: string;
  upgradeUrl: string;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
  isJapanLoginEnabled?: boolean;
};

const AccountDropdown = (props: NavMobileProps) => {
  const { user = DEFAULT_USER } = props;
  const { subscriptionsData = [] } = user;

  const userIsLoggedIn = user?.isLoggedIn && !user.isSubscriber;
  const userIsSubscriber = user?.isLoggedIn && user.isSubscriber;
  const userHasCorporateHubAccess =
    user.isSubscriber &&
    subscriptionsData.some((sub: SubscriptionData) => sub.corporateHubAccess);
  const translateMessage = useTranslateMessage();

  if (userIsLoggedIn) {
    return (
      <UserOptionsDropdownMobile
        {...props}
        user={user}
        sections={getLocalizedMenuSections(
          USER_OPTIONS_MENU_ITEMS_LOGGED_IN,
          translateMessage
        )}
      />
    );
  }

  if (userHasCorporateHubAccess) {
    return (
      <UserOptionsDropdownMobile
        {...props}
        user={user}
        sections={getLocalizedMenuSections(
          USER_OPTIONS_MENU_ITEMS_SUBSCRIBER_WITH_HUB_ACCESS,
          translateMessage
        )}
      />
    );
  }

  if (userIsSubscriber) {
    return (
      <UserOptionsDropdownMobile
        {...props}
        user={user}
        sections={getLocalizedMenuSections(
          USER_OPTIONS_MENU_ITEMS_SUBSCRIBER,
          translateMessage
        )}
      />
    );
  }

  return null;
};

export type MobileMenuHandle = {
  getIsMenuOpen: () => boolean;
};

const NavMobile = forwardRef<MobileMenuHandle, NavMobileProps>(
  function NavMobileComponent(props: NavMobileProps, ref) {
    const {
      isWeekend,
      region = REGIONS.US,
      searchClient,
      theme = THEMES.DARK,
      user = DEFAULT_USER,
      trackingFunction,
      countryCode,
      subscriptionUrl,
      upgradeUrl,
      handleShowSignInModal,
      setSignInButtonRef,
      isJapanLoginEnabled,
    } = props;

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSignInAndUserOptionsOpen, setIsSignInAndUserOptionsOpen] =
      useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const isScreenLockedForScrolling = isMenuOpen || isSignInAndUserOptionsOpen;
    const translateMessage = useTranslateMessage();
    const { locale } = useNavContext();
    const showSearchMobile = locale === 'en';
    const showCoJpSearchMobile = locale === JAPAN_LOCALE_STRING;

    useImperativeHandle(ref, () => ({
      getIsMenuOpen: () => isScreenLockedForScrolling,
    }));

    useEffect(() => {
      // This is for preventing scroll on body content when a menu is open
      document.body.style.overflowY = isScreenLockedForScrolling
        ? 'hidden'
        : 'auto';
    }, [isScreenLockedForScrolling]);

    useEffect(() => {
      if (!isMenuOpen) {
        setIsSearchActive(false);
      }
    }, [isMenuOpen]);

    const cx = classes([
      styles.navMobileContainer,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
        [styles.open]: isMenuOpen || isSignInAndUserOptionsOpen,
      },
    ]);

    const toggleMenu = useCallback(() => {
      setIsSignInAndUserOptionsOpen(false);
      setIsMenuOpen((prevIsMenuOpen) => !prevIsMenuOpen);
    }, []);

    const toggleSignInAndUserOptions = useCallback(() => {
      setIsMenuOpen(false);

      if (user.isLoggedIn) {
        setIsSignInAndUserOptionsOpen(
          (prevIsSignInAndUserOptionsOpen) => !prevIsSignInAndUserOptionsOpen
        );
      }
    }, [user.isLoggedIn]);

    return (
      <FocusTrap
        active={isScreenLockedForScrolling}
        focusTrapOptions={{
          fallbackFocus: `body`,
          clickOutsideDeactivates: true,
          escapeDeactivates: true,
        }}
      >
        <div className={cx}>
          <div className={styles.mobileNavContent}>
            <LogoBarMobile
              region={region}
              isMenuOpen={isMenuOpen}
              isAccountMenuOpen={isSignInAndUserOptionsOpen}
              isWeekend={isWeekend}
              user={user}
              theme={theme}
              toggleMenu={toggleMenu}
              toggleAccountMenu={toggleSignInAndUserOptions}
              trackingFunction={trackingFunction}
              subscriptionUrl={subscriptionUrl}
              upgradeUrl={upgradeUrl}
              countryCode={countryCode}
              handleShowSignInModal={handleShowSignInModal}
              setSignInButtonRef={setSignInButtonRef}
              isJapanLoginEnabled={isJapanLoginEnabled}
            />
            {isMenuOpen && (
              <>
                <RegionPickerMobile
                  trackingFunction={trackingFunction}
                  region={region}
                  theme={theme}
                />
                {showSearchMobile && (
                  <SearchMobile
                    isSearchActive={isSearchActive}
                    setIsSearchActive={setIsSearchActive}
                    trackingFunction={trackingFunction}
                    searchClient={searchClient}
                    countryCode={countryCode}
                  />
                )}
                {showCoJpSearchMobile && <SearchMobileJapan />}
                {!isSearchActive && <MenuMobile region={region} {...props} />}
              </>
            )}
            {isSignInAndUserOptionsOpen && (
              <div
                aria-label={translateMessage({
                  id: 'media-ui-nav_arialabel_userAccountMenu',
                  defaultMessage: 'User Account Menu',
                })}
              >
                <AccountDropdown {...props} />
              </div>
            )}
          </div>
        </div>
      </FocusTrap>
    );
  }
);

export default NavMobile;
